import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Spin } from 'antd';
import styled from '@emotion/styled';

import { documentsQueries } from 'api';
import { APIDocument, DocumentFileType } from 'types';

import { DocumentCommentItems } from './DocumentCommentItems';

const CustomContainer = styled.div`
  padding: 16px 32px;
`;

interface DocumentClientCommentsProps {
  document: APIDocument;
}

const CONTAINER_HEIGHT = 400;
const PAGE_SIZE = 10;
const ITEM_COUNT = 4;
const SCROLL_THRESHOLD = 80;

export const DocumentClientComments = ({ document }: DocumentClientCommentsProps) => {
  const { t } = useTranslation();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: documentsQueries.getComments(document.id).queryKey,
    queryFn: documentsQueries.getComments(document.id, { per_page: PAGE_SIZE }).queryFn,
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined
  });

  if (document.file_type !== DocumentFileType.PDF) return null;

  const count = data?.pages.reduce((total, page) => total + (page.results?.length || 0), 0) || 0;

  const handleScroll = (e: React.UIEvent<HTMLElement, UIEvent>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Spin spinning={isLoading}>
      <CustomContainer>
        <div>
          {t('comments')}{' '}
          <span>
            ({count}
            {hasNextPage ? '+' : ''})
          </span>
        </div>

        <DocumentCommentItems
          document={document}
          comments={data?.pages.flatMap((page) => page.results) || []}
          listProps={
            count && count >= ITEM_COUNT
              ? { height: CONTAINER_HEIGHT, onScroll: handleScroll }
              : {}
          }
        />
      </CustomContainer>
    </Spin>
  );
};
